import { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import Contents from "./Contents";
import Tags, { Tag } from  "../public/Tags";
import Search from "../public/Search";
import { ContentIF, TagIF } from '../../types';
import { TagIconContainer, TagIcon, MenuIconContainer, MenuIcon, InfoIconContainer, InfoIcon, SearchIcon } from '../../icons';
import axios from 'axios';
import {HEADER_BOTTOM, HEADER_HEIGHT} from '../../public/Header';
import { useTheme } from '../../theme/ThemeProvider';
import ThemeToggle from '../../public/ThemeToggle';
import Copyright from '../../public/Copyright';

export const ONE_PAGE_MAX = 12;

/* ---------------- 애니메이션 ---------------- */

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

/* ---------------- 전체 배경 ---------------- */

const BG = styled.div`
  height: 100vh;                 /* 🔥 전체 화면 고정 */
  display: flex;
  flex-direction: column;
  overflow: hidden;              /* 🔥 전체 스크롤 막기 */
  background: linear-gradient(135deg, 
    ${props => props.theme.ROOT_BACKGROUND.FIRST} 0%, 
    ${props => props.theme.ROOT_BACKGROUND.LAST} 100%);
  font-family: 'Pretendard', -apple-system, sans-serif;
`;

/* ---------------- Header ---------------- */

export const Head = styled.div`
  background: ${props => props.theme.HEADER.BACKGROUND};
  backdrop-filter: blur(12px);

  display: flex;
  justify-content: space-between;
  align-items: center;

  padding: 0 2rem;
  width: 100%;
  height: ${HEADER_HEIGHT}px;

  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;

  border-bottom: 1px solid rgba(0, 0, 0, 0.05);

  padding: 0 4rem;
  box-sizing: border-box; // padding이 너비에 포함되게끔
`;

const BlogName = styled.h1`
  font-size: 1.5rem;
  font-weight: 800;
  color: ${props => props.theme.HEADER.BLOG_NAME};
  letter-spacing: -1px;
  cursor: pointer;
`;

/* ---------------- Body ---------------- */

const Body = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;

  margin-top: ${HEADER_HEIGHT}px;
  height: calc(100vh - ${HEADER_HEIGHT}px);
  overflow: hidden;
`;

/* ---------------- BOX 공통 ---------------- */

const Box = styled.div`
  background: ${props => props.theme.BOX_BACKGROUND};
  border-radius: 1rem;
  padding: 1.2rem;
  box-shadow: 0 4px 6px -1px ${props => props.theme.BOX_SHADOW.FIRST},
              0 2px 4px -1px ${props => props.theme.BOX_SHADOW.LAST};
  border: 1px solid ${props => props.theme.BOX_BORDER};
`;

const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const Taglist = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 0;

  margin: 0;              /* 기본 마진 제거 */
  border-top: none;       /* 헤더 경계선과 겹치지 않게 상단 테두리 제거 */
  border-left: none;      /* 양 옆 테두리 제거 (전체 너비일 경우) */
  border-right: none;
  box-shadow: none;

  background: ${props => props.theme.HEADER.BACKGROUND};
`;

/* ---------------- Content 영역 ---------------- */

const ContentList = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;

  height: 100%;
  overflow-y: auto;

  padding: 2rem 1rem 5rem 1rem; 
  box-sizing: border-box;

  /* 스크롤바가 보기 싫다면 투명하게 처리 */
  &::-webkit-scrollbar {
    width: 0px; /* 스크롤바 너비를 0으로 하여 공간 점유 방지 */
    background: transparent;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 10px;
  }
`;

/* ---------------- Page ---------------- */

const Pages = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 1rem;
`;

const PageBtn = styled.div<{$isCur:boolean}>`
  width: 2rem;
  height: 2rem;
  border: 1px solid ${props => props.theme.PAGE_BTN.BORDER};
  border-radius: 7px;
  background-color: ${({$isCur}) => ($isCur ? props => props.theme.PAGE_BTN.BACKGROUND.CURRENT : props => props.theme.PAGE_BTN.BACKGROUND.NOT_CURRENT)};
  box-shadow: 3px 2px 2px rgba(0, 0, 0, 0.1);
  color: ${props => props.theme.PAGE_BTN.FONT_COLOR};

  font-weight: 600;
  
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    background-color: ${props => props.theme.PAGE_BTN.HOVER.BACKGROUND};
    color: ${props => props.theme.PAGE_BTN.HOVER.FONT_COLOR};
  }
`;

const MagicTool = styled.div`
  position: fixed;
  bottom: 1rem;
  right: 1rem;
  width: 3rem;
  height: 3rem;
  background-color: ${props => props.theme.MAGIC_TOOL.BACKGROUND};
  color: ${props => props.theme.MAGIC_TOOL.FONT_COLOR};
  border-radius: 50%;
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center; 
  cursor: pointer;
  transition: transform 0.2s;

  &:active {
    transform: scale(0.9);
  }
`;

const EachMagicTool = styled.div<{ $index: number, $active: boolean, $isOn?: boolean}>`
  position: fixed;
  bottom: ${({ $index, $active }) => ($active ? 1.5 + $index * 3.2 : 1.5)}rem; /* MagicTool에서 위로 3rem씩 띄우기 */
  right: 1rem;
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  background-color: ${({$isOn}) => $isOn ? props => props.theme.EACH_MAGIC_TOOL.ACTIVATED.BACKGROUND : props => props.theme.EACH_MAGIC_TOOL.BACKGROUND};
  color: ${({$isOn}) => $isOn ? props => props.theme.EACH_MAGIC_TOOL.ACTIVATED.FONT_COLOR : props => props.theme.EACH_MAGIC_TOOL.FONT_COLOR};

  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9998;
  
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  transform: ${({ $active }) => ($active ? "scale(1)" : "scale(0) translateY(20px)")};
  pointer-events: ${({ $active }) => ($active ? "auto" : "none")};
`;

/* ---------------- Home Component ---------------- */

function Home() {
  const [tags, setTags] = useState<TagIF[]>([]);
  const [selectedTags, setSelectedTags] = useState<TagIF[]>([]);
  const [searchedTerm, setSearchedTerm] = useState<string>("");
  const [totalContents, setTotalContents] = useState<number>(0);
  const [totalViews, setTotalViews] = useState<number>(0);
  const [allContents, setAllContents] = useState<ContentIF[]>([]);
  const [loading, setLoading] = useState(true);
  const [visibleLength, setVisibleLength] = useState<number>(0);
  const [pages, setPages] = useState<number[]>([]);
  const [curPage, setCurPage] = useState<number>(1);
  const [magicActive, setMagicActive] = useState<boolean>(false);
  const [searchActive, setSearchActive] = useState<boolean>(false);
  const [tagActive, setTagActive] = useState<boolean>(false);

  const navigate = useNavigate();
  const { theme, toggle } = useTheme();

  const getTags = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/tags/getTags`);
      setTags(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    getTags();
    axios.get(`${process.env.REACT_APP_API_URL}/contents/getContents`)
      .then(res => {
        setAllContents(res.data);
        // 여기서 통계 바로 계산 (자식한테 시키지 않음)
        setTotalContents(res.data.length);
        const views = res.data.reduce((acc:number, cur:ContentIF) => acc + (cur.view || 0), 0);
        setTotalViews(views);
        setLoading(false);
      });
  }, []); // 딱 한 번만 실행

  // 콘텐츠 페이지 버튼 
  useEffect(() => {
    const temp = [];
    for (let i=1; i<visibleLength/ONE_PAGE_MAX + 1; i++) {
      temp.push(i);
    }
    setPages(temp);

  }, [visibleLength]);

  useEffect(() => {
    setCurPage(1); // 검색어나 태그가 바뀌면 페이지 초기화
  }, [searchedTerm, selectedTags]);

  const toggleMagic = () => {
    setMagicActive(prev => !prev);
  }

  const showMagicTool = magicActive ? (
    <>
      <EachMagicTool $index={3} $active={true} onClick={() => {navigate('/aboutme');}}>
        <InfoIcon size={16}/>
      </EachMagicTool>
      <EachMagicTool $index={2} $active={true} $isOn={searchActive} onClick={() => {setSearchActive(prev => !prev);}}>
        <SearchIcon size={16}/>
      </EachMagicTool>
      <EachMagicTool $index={1} $active={true} $isOn={tagActive} onClick={() => {setTagActive(prev => !prev);}}>
        <TagIcon size={16} />
      </EachMagicTool>
    </>
  ) : null;

  const showSearch = searchActive ? (
    <Head>
      <Search
            searchedTerm={searchedTerm}
            onSearchedTerm={setSearchedTerm}
            styleFor={process.env.REACT_APP_STYLE_HOME_SEARCH}
            placeholder="🔍"
      />
    </Head>
  ) : (
    <Head>
      <BlogName onClick={() => navigate("/")}>Lamiro</BlogName>
      <ThemeToggle toggle={toggle} mode={theme} />
    </Head>
  );

  const showTag = tagActive ? (
    <Taglist>
      <TagContainer>
        <Tags 
          tags={tags} 
          selectedTags={selectedTags} 
          onSelectedTags={setSelectedTags}
        />
      </TagContainer>
    </Taglist>
  ) : null;

  return (
    <BG>
      {showSearch}
        <Body>
          {showTag}
          <ContentList>
            <Contents
              contents={allContents}
              loading={loading}      
              selectedTags={selectedTags}
              searchedTerm={searchedTerm}
              curPage={curPage}
              onVisibleLength={setVisibleLength}
            />
            <Pages>
                {pages.map(page => (
                  <PageBtn $isCur={page === curPage} onClick={() => setCurPage(page)}>{page}</PageBtn>
                ))}
            </Pages>
            <Copyright />
          </ContentList>
          <div style={{columnGap: "3rem"}}>
            {showMagicTool}
            <MagicTool onClick={toggleMagic}>
              <MenuIcon />
            </MagicTool>
          </div>
        </Body>
    </BG>
  );
}

export default Home;